import Router, { Route, RouteConfig } from 'vue-router'

import { discountAssociationParametersRoutes, discountRoutes } from '@invivodf/common/src/contexts/discount'
import { logisticRoutes } from '@invivodf/common/src/contexts/logistic-offer/routes/logistic-routes'

import VMicroFrontProvider from '@b2ag/micro-front-tools/src/components/VMicroFrontProvider.vue'

import { GuardAuthType } from '@invivodf/front-seller-auth/src/isAuthorized'
import { BRAND_MANAGE_SCOPES } from '@invivodf/front-seller-auth/src/BRAND_MANAGE_SCOPES'
import { SCA_MANAGE_SCOPES } from '@invivodf/front-seller-auth/src/SCA_MANAGE_SCOPES'

import IRouterNames, { IRouterURL, ZRouterURL } from '@/retail/application/types/config/router'
import { UPDATE_CLIENTS } from '@/retail/contexts/user/domain/constants/SCOPES'

import VParameters from '@/retail/contexts/parameters/views/VParameters/VParameters.vue'
import VParametersList from '@/retail/contexts/parameters/views/VParametersList/VParametersList.vue'
import VImportReports from '@/retail/contexts/parameters/views/VImportReports/VImportReports.vue'
import VImportReport from '@/retail/contexts/parameters/views/VImportReport.vue'
import VCatalog from '@/retail/application/views/VCatalog/VCatalog.vue'
import VCustomers from '@/retail/contexts/customers/views/VCustomers/VCustomers.vue'
import VCustomerFile from '@/retail/contexts/customers/views/VCustomerFile/VCustomerFile.vue'
import ZCustomers from '@/retail/contexts/customers/views/ZCustomers/ZCustomers.vue'
import VLayoutDefault from '@/retail/application/views/VLayoutDefault/VLayoutDefault.vue'
import ZLayoutDefault from '@/retail/application/views/VLayoutDefault/ZLayoutDefault.vue'
import VLoginCallback from '@/retail/application/views/VLoginCallback/VLoginCallback.vue'
import VNotAuthorized from '@/retail/application/views/VNotAuthorized/VNotAuthorized.vue'
import VNotFound from '@/retail/application/views/VNotFound/VNotFound.vue'
import VProduct from '@/retail/application/views/VProduct/VProduct.vue'

// Retail route set — mounted only when getContextForBrand resolves to Retail
// (jardiland/gammvert). Never coexists with the b2ag route set, so the b2ag
// route names/paths (home, parameters, oauth-callback, webhooks, not-found) are
// resolved by construction. `getRouter` is injected lazily so the micro-front
// props can reference the router instance created in router.ts.
export function getRetailRoutes(getRouter: () => Router): RouteConfig[] {
  return [
    {
      path: IRouterURL.OAUTH_CALLBACK,
      name: IRouterNames.OAUTH_CALLBACK,
      component: VLoginCallback,
      meta: {
        allowAnonymousAccess: true,
      },
    },
    {
      path: IRouterURL.NOT_AUTHORIZED,
      name: IRouterNames.NOT_AUTHORIZED,
      component: VNotAuthorized,
    },
    {
      path: ZRouterURL.Z_HOME,
      name: IRouterNames.Z_HOME,
      component: ZLayoutDefault,
      children: [
        {
          path: ZRouterURL.Z_CUSTOMERS,
          name: IRouterNames.Z_CUSTOMERS,
          component: ZCustomers,
          meta: {
            auth: {
              permissions: [UPDATE_CLIENTS],
              type: GuardAuthType.someOf,
            },
          },
        },
      ],
    },
    {
      path: IRouterURL.HOME,
      name: IRouterNames.HOME,
      component: VLayoutDefault,
      children: [
        {
          path: IRouterURL.CATALOG,
          name: IRouterNames.CATALOG,
          component: VCatalog,
        },
        {
          path: IRouterURL.PRODUCT,
          name: IRouterNames.PRODUCT,
          component: VProduct,
          props(route: Route) {
            return { sku: route.params?.sku }
          },
        },
        {
          path: IRouterURL.SCA_SELLERS,
          name: IRouterNames.SCA_SELLERS,
          component: VMicroFrontProvider,
          props: () => ({
            microFrontName: 'sca-sellers',
            routeName: 'sca-sellers',
            router: getRouter(),
          }),
          meta: {
            auth: {
              permissions: SCA_MANAGE_SCOPES,
              type: GuardAuthType.someOf,
            },
          },
        },
        {
          path: IRouterURL.CLIENTS,
          name: IRouterNames.CLIENTS,
          component: VCustomers,
          meta: {
            auth: {
              permissions: [UPDATE_CLIENTS],
              type: GuardAuthType.someOf,
            },
          },
        },
        {
          path: IRouterURL.CLIENT,
          name: IRouterNames.CLIENT,
          component: VCustomerFile,
          props(route: Route) {
            return { id: route.params?.id }
          },
          meta: {
            auth: {
              permissions: [UPDATE_CLIENTS],
              type: GuardAuthType.someOf,
            },
          },
        },
        ...discountRoutes,
        ...logisticRoutes,
        {
          path: 'webhooks',
          name: 'webhooks',
          component: VMicroFrontProvider,
          props: () => ({
            microFrontName: 'webhooks',
            routeName: 'webhooks',
            router: getRouter(),
          }),
        },
        {
          path: 'webhooks/:webhookId',
          name: 'webhook',
          component: VMicroFrontProvider,
          props: (route) => ({
            microFrontName: 'webhooks',
            routeName: `webhooks/${route.params.webhookId}`,
            router: getRouter(),
          }),
        },
        {
          path: IRouterURL.BRAND_REDIRECTIONS,
          name: IRouterNames.BRAND_REDIRECTIONS,
          component: VMicroFrontProvider,
          props: () => ({
            microFrontName: 'brand-redirections',
            routeName: 'brand-redirections',
            router: getRouter(),
          }),
          meta: {
            auth: {
              permissions: BRAND_MANAGE_SCOPES,
              type: GuardAuthType.someOf,
            },
          },
        },
        {
          path: 'parameters',
          component: VParameters,
          children: [
            {
              path: '',
              name: 'parameters',
              component: VParametersList,
            },
            {
              path: 'import-reports',
              name: 'import-reports',
              component: VImportReports,
            },
            {
              path: 'import-reports/detail/:importType/:importId',
              name: 'import-report',
              component: VImportReport,
            },
            ...discountAssociationParametersRoutes,
          ],
        },
      ],
    },
    {
      path: '/',
      component: VLayoutDefault,
      children: [
        {
          path: IRouterURL.NOT_AUTHORIZED,
          name: IRouterNames.NOT_AUTHORIZED,
          component: VNotAuthorized,
        },
        {
          path: IRouterURL.NOT_FOUND,
          name: IRouterNames.NOT_FOUND,
          component: VNotFound,
        },
      ],
    },
  ]
}
